import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  ButtonComponent,
  CheckboxComponent,
  FormGroupComponent,
  FormsUtil,
  InputButton,
  InputComponent,
  InputType,
  MbsSize,
  MbsValidators,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  SelectComponent,
  ToastService
} from 'mbs-ui-kit';
import { ApplyPresetModalComponent } from 'mbs-ui-shared@components/admin-permission-presets/apply-preset-modal/apply-preset-modal.component';
import { AdminPermissionPresetsFacade } from 'mbs-ui-shared@facades/admin-permission-presets.facade';
import { AdministratorsFacade } from 'mbs-ui-shared@facades/administrators.facade';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { AccountTypeEnum, Company, Permissions, PermissionsEnum } from 'mbs-ui-shared@models';
import AdministratorInCamelCase, { permissionsListToObject, permissionsObjectToList } from 'mbs-ui-shared@models/AdministratorInCamelCase';
import { AdminPermissionPreset } from 'mbs-ui-shared@models/AdminPermissionPreset';
import { AdministratorService } from 'mbs-ui-shared@services/administrator.service';
import { allAdministratorPermissions } from 'mbs-ui-shared@utils/administrator-permissions.util';
import { from, noop, Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, startWith, switchMap, take } from 'rxjs/operators';
import { CompanyWizardFacade } from 'wizards/services/entity-wizards/company/company-wizard.facade';
import { CompanyAdministratorPermissionsOption } from '../../models/company-wizard-models';
import {
  ManualPermissionsModalComponent,
  ManualPermissionsModalData
} from '../manual-permissions-modal/manual-permissions-modal.component';

type SelectOption<T> = {
  description?: string;
  id: T;
  name: string;
};

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    CheckboxComponent,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    ModalComponent,
    ModalFooterDirective,
    NgbTooltipModule,
    ReactiveFormsModule,
    SelectComponent
  ],
  selector: 'mbs-create-administrator-modal',
  standalone: true,
  templateUrl: './create-administrator-modal.component.html'
})
export class CreateAdministratorModalComponent {
  private readonly ability = inject(AbilityService);
  private readonly administratorService = inject(AdministratorService);
  private readonly administratorsFacade = inject(AdministratorsFacade);
  private readonly auth = inject(AuthFacade);
  private readonly companyWizardFacade = inject(CompanyWizardFacade);
  private readonly presetsFacade = inject(AdminPermissionPresetsFacade);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly modalService = inject(ModalService);
  private readonly toast = inject(ToastService);

  private readonly modal = viewChild(ModalComponent);

  public readonly loading: WritableSignal<boolean> = signal(false);
  // The tree rebuilds itself from the keys it is given, so it needs every permission present, unchecked ones included
  public readonly permissions: WritableSignal<Permissions> = signal(permissionsListToObject([]));

  private committedPermissionsOption: CompanyAdministratorPermissionsOption = null;
  public readonly passwordGenerating: WritableSignal<boolean> = signal(false);
  public readonly passwordRules: WritableSignal<string[]> = signal([]);
  public readonly passwordVisible: WritableSignal<boolean> = signal(false);

  public readonly passwordType = computed(() => (this.passwordVisible() ? InputType.Text : InputType.Password));
  public readonly passwordButtons = computed<InputButton[]>(() => [
    {
      icon: this.passwordVisible() ? 'fa fa-eye-slash' : 'fa fa-eye',
      id: 'company-administrator-password-visibility',
      disabled: false,
      type: 'secondary'
    }
  ]);

  public readonly accountTypes: SelectOption<AccountTypeEnum>[] = [
    {
      description: this.getText('accountTypeInternalDescription'),
      id: AccountTypeEnum.Internal,
      name: this.getText('accountTypeInternal')
    },
    {
      description: this.getText('accountTypeExternalDescription'),
      id: AccountTypeEnum.External,
      name: this.getText('accountTypeExternal')
    }
  ];

  public readonly permissionsOptions: SelectOption<CompanyAdministratorPermissionsOption>[] = [
    { id: CompanyAdministratorPermissionsOption.Preset, name: this.getText('permissionsPreset') },
    { id: CompanyAdministratorPermissionsOption.Manual, name: this.getText('permissionsManual') },
    { id: CompanyAdministratorPermissionsOption.All, name: this.getText('permissionsAll') }
  ];

  public readonly form = new FormGroup({
    accountType: new FormControl<AccountTypeEnum>(null, [Validators.required]),
    permissionsOption: new FormControl<CompanyAdministratorPermissionsOption>(null),
    // updateOn 'blur' keeps fetchValidateEmail from firing on every keystroke; the sidepanel pays for 'change' with a hand-rolled result cache.
    email: new FormControl('', {
      validators: [Validators.required, MbsValidators.emailValidator],
      asyncValidators: [this.emailAsyncValidator.bind(this)],
      updateOn: 'blur'
    }),
    password: new FormControl('', {
      validators: [Validators.required],
      asyncValidators: [MbsValidators.passwordStrengthValidator(this.auth)]
    }),
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    enabled: new FormControl(true),
    sendInstruction: new FormControl(true)
  });

  constructor() {
    this.auth
      .getPasswordRules()
      .pipe(untilDestroyed(this))
      .subscribe((rules) => this.passwordRules.set(rules));
  }

  public passwordVisibilityClickHandler(): void {
    this.passwordVisible.update((visible) => !visible);
  }

  public generatePasswordClickHandler(): void {
    this.passwordGenerating.set(true);

    this.auth
      .generatePassword()
      .pipe(
        finalize(() => this.passwordGenerating.set(false)),
        untilDestroyed(this)
      )
      .subscribe((password) => this.form.controls.password.patchValue(password));
  }

  public saveClickHandler(): void {
    FormsUtil.triggerValidation(this.form);

    // Email and password are validated asynchronously, so right after triggerValidation the form is PENDING and form.valid is still false.
    this.form.statusChanges
      .pipe(
        startWith(this.form.status),
        filter((status) => status !== 'PENDING'),
        take(1),
        untilDestroyed(this)
      )
      .subscribe((status) => status === 'VALID' && this.createAdministrator());
  }

  public permissionsOptionChangeHandler(option: CompanyAdministratorPermissionsOption): void {
    switch (option) {
      case CompanyAdministratorPermissionsOption.Manual:
        return this.chooseManually(option);
      case CompanyAdministratorPermissionsOption.Preset:
        return this.choosePreset(option);
      default:
        this.committedPermissionsOption = option;
    }
  }

  public cancelClickHandler(): void {
    this.modal().close();
  }

  // Private section
  private chooseManually(option: CompanyAdministratorPermissionsOption): void {
    const data: ManualPermissionsModalData = {
      accountType: this.form.controls.accountType.value,
      permissions: this.permissions()
    };

    from(this.modalService.openCustom<Permissions>(ManualPermissionsModalComponent, { data, size: MbsSize.sm }))
      .pipe(
        catchError(() => of(null)),
        take(1),
        untilDestroyed(this)
      )
      .subscribe((permissions) => {
        if (!permissions) return void this.revertPermissionsOption();

        this.permissions.set(permissions);
        this.committedPermissionsOption = option;
      });
  }

  private choosePreset(option: CompanyAdministratorPermissionsOption): void {
    this.presetsFacade
      .init()
      .pipe(
        switchMap(() => from(this.modalService.openCustom<AdminPermissionPreset>(ApplyPresetModalComponent, { size: MbsSize.sm }))),
        catchError(() => of(null)),
        take(1),
        untilDestroyed(this)
      )
      .subscribe((preset) => {
        if (!preset) return void this.revertPermissionsOption();

        this.permissions.set(permissionsListToObject(preset.permissions));
        this.committedPermissionsOption = option;
        this.toast.success(this.i18nPipe.transform('shared:administrators.presets.presetApplied'));
      });
  }

  private revertPermissionsOption(): void {
    this.form.controls.permissionsOption.setValue(this.committedPermissionsOption, { emitEvent: false });
  }

  private createAdministrator(): void {
    const company = this.companyWizardFacade.company();
    const value = this.form.getRawValue();
    const administrator = {
      accountType: value.accountType,
      companies: company ? [{ id: company.id, name: company.name } as Company] : [],
      email: value.email,
      enabled: value.enabled,
      firstName: value.firstName,
      lastName: value.lastName,
      password: value.password,
      permissions: this.selectedPermissions(value.accountType),
      sendInstruction: value.sendInstruction
    } as AdministratorInCamelCase;

    this.setLoading(true);

    this.administratorsFacade
      .create(administrator)
      .pipe(
        finalize(() => this.setLoading(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (created) => {
          this.toast.success(this.i18nPipe.transform('shared:notifications.successfullyCreated'));
          this.modal().save(created);
        },
        error: noop
      });
  }

  private selectedPermissions(accountType: AccountTypeEnum): PermissionsEnum[] {
    if (this.committedPermissionsOption !== CompanyAdministratorPermissionsOption.All) return permissionsObjectToList(this.permissions());

    return allAdministratorPermissions({
      accountType,
      can: (permission) => this.ability.can('read', permission),
      chat: this.auth.currentUserValue?.ProviderInfo?.Chat
    });
  }

  private emailAsyncValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    if (!control.value) return of(null);

    return this.administratorService.fetchValidateEmail(control.value).pipe(
      map((isExist: boolean) => (isExist ? { email: { message: this.i18nPipe.transform('shared:validation.existing_email') } } : null)),
      catchError(() => of(null))
    );
  }

  private setLoading(state: boolean): void {
    this.loading.set(state);
    this.form[state ? 'disable' : 'enable']();
  }

  private getText(key: string): string {
    return this.i18nPipe.transform(`wizards:entityWizards.company.administratorsStep.createModal.${key}`);
  }
}
